import type {
  CreateDeliveryRequest,
  DeliveryNoteResponse,
  DeliveryProofRequest,
  PostDeliveryRequest,
  UpdateDeliveryRequest,
} from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

import { ORDERS_KEY } from './quotes-api';

/** SAL-03 data layer — the counted-not-priced document. */

export const DELIVERIES_KEY = ['delivery-notes'] as const;

export interface DeliveryListFilters {
  salesOrderId?: string | undefined;
  status?: DeliveryNoteResponse['status'] | undefined;
  /** Wave 3B — the list's window on the delivery date, both inclusive. */
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
}

/**
 * Wave 3B — whether the list draws its search box. The contract does not
 * take `q` yet; the moment `DeliveryListFilters` gains it this annotation
 * becomes `true` and the typecheck demands the flip, so the box appears with
 * the route rather than before it.
 */
export const DELIVERIES_SEARCHABLE: 'q' extends keyof DeliveryListFilters ? true : false = false;

function deliveryQuery(filters: DeliveryListFilters) {
  return {
    ...(filters.salesOrderId ? { salesOrderId: filters.salesOrderId } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
  };
}

/** SAL-03's list, one page at a time (NFR-42). */
export function useDeliveries(filters: DeliveryListFilters, page: number, pageSize: number) {
  return useQuery({
    queryKey: [...DELIVERIES_KEY, filters, page, pageSize],
    queryFn: async () => {
      const response = await api.deliveryNotes.list({ query: { ...deliveryQuery(filters), page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/** The export's page read — the same query the screen shows, imperative so `fetchAllPages` can walk it. */
export async function fetchDeliveriesPage(
  filters: DeliveryListFilters,
  page: number,
  pageSize: number,
): Promise<{ rows: DeliveryNoteResponse[]; total: number }> {
  const response = await api.deliveryNotes.list({ query: { ...deliveryQuery(filters), page, pageSize } });
  if (response.status !== 200) throwApiError(response.status, response.body);
  return { rows: response.body.deliveries, total: response.body.total };
}

/** Wave 3B — SAL-03's list tiles over the whole register (PAGINATION.md §3 step 10). */
export function useDeliveryStats() {
  return useQuery({
    queryKey: [...DELIVERIES_KEY, 'stats'],
    queryFn: async () => {
      const response = await api.deliveryNotes.stats();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useDelivery(deliveryId: string | undefined) {
  return useQuery({
    queryKey: [...DELIVERIES_KEY, 'one', deliveryId],
    enabled: Boolean(deliveryId),
    queryFn: async (): Promise<DeliveryNoteResponse> => {
      const response = await api.deliveryNotes.get({ params: { deliveryId: deliveryId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useCreateDelivery() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateDeliveryRequest): Promise<DeliveryNoteResponse> => {
      const response = await api.deliveryNotes.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DELIVERIES_KEY }),
  });
}

export function useUpdateDelivery(deliveryId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateDeliveryRequest): Promise<DeliveryNoteResponse> => {
      const response = await api.deliveryNotes.update({ params: { deliveryId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DELIVERIES_KEY }),
  });
}

export function useDeleteDelivery(deliveryId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await api.deliveryNotes.remove({ params: { deliveryId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DELIVERIES_KEY }),
  });
}

/**
 * The leftover drafts, deleted one by one through the ordinary delete route
 * — no bulk endpoint, and none wanted: each is a draft the server refuses
 * to delete if it is no longer one. Stops at the first refusal so the
 * screen can say which code would not go.
 */
export function useDeleteDeliveries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (deliveryIds: readonly string[]): Promise<void> => {
      for (const deliveryId of deliveryIds) {
        const response = await api.deliveryNotes.remove({ params: { deliveryId } });
        if (response.status !== 204) throwApiError(response.status, response.body);
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: DELIVERIES_KEY }),
  });
}

/**
 * Confirm delivery — the posting step (BR-SAL-03-03/-04): stock, COGS and
 * the order's fulfillment all move, so the orders refresh too. The body is
 * the override's reason when the note takes a line below zero
 * (BR-INV-04-08 + `inventory.override_negative_stock`); empty otherwise.
 */
export function usePostDelivery(deliveryId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: PostDeliveryRequest = {}): Promise<DeliveryNoteResponse> => {
      const response = await api.deliveryNotes.post({ params: { deliveryId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DELIVERIES_KEY });
      void queryClient.invalidateQueries({ queryKey: ORDERS_KEY });
    },
  });
}

/**
 * Wave 5B — proof of delivery (SAL-03 §5d): who received the goods, when,
 * their note and the signature attachment, written on a CONFIRMED note
 * through its own route (the one post-confirmation write beside the notes).
 * Nothing the books count moves, so only the note itself refreshes.
 */
export function useRecordDeliveryProof(deliveryId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: DeliveryProofRequest): Promise<DeliveryNoteResponse> => {
      const response = await api.deliveryNotes.proof({ params: { deliveryId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DELIVERIES_KEY }),
  });
}

/** BR-SAL-03-07's tail: cancel a CONFIRMED delivery, reason required (GEN-72). */
export function useCancelDelivery(deliveryId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string): Promise<DeliveryNoteResponse> => {
      const response = await api.deliveryNotes.cancel({ params: { deliveryId }, body: { reason } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DELIVERIES_KEY });
      void queryClient.invalidateQueries({ queryKey: ORDERS_KEY });
    },
  });
}
